import React from 'react'
import { ChevronsUpDown, Send, Minus } from 'lucide-react'
import type { Invitee } from '../types/invitee'
import { formatPhoneNumber, formatCurrency, formatDateTimeForDisplay } from '../utils/inviteeFormatters'
import { ALLOW_PROGRESS_STATUSES } from '../utils/inviteeConstants'
import { cn } from '@/lib/utils/helpers'
import { TableSearchFilterEmptyTableRow } from '@/components/ui/TableSearchFilterEmptyState'
import styles from '../styles/invitesParity.module.css'

/** `table-layout:fixed` column weights (sum 100%) — balances Action vs data columns */
const INVITEE_COL_WIDTHS_PCT_WITH_ACTION = [
  2, 6, 8, 10, 7, 8, 8, 6, 5, 5, 3, 5, 5, 6, 8, 8,
] as const
const INVITEE_COL_WIDTHS_PCT_NO_ACTION = [2, 6, 9, 11, 8, 8, 8, 6, 5, 5, 4, 5, 6, 6, 11] as const

interface InviteeTableProps {
  invitees: Invitee[]
  onSort: (orderBy: string) => void
  sortBy?: 'asc' | 'desc'
  orderBy?: string
  onResend?: (inviteCode: string) => void
  onEdit?: (inviteCode: string, parentInviteCode?: string) => void
  onAddCoApplicant?: (invite: Invitee) => void
  onApplyNow?: (inviteCode: string) => void
  showActions?: boolean
}

export function InviteeTable({
  invitees,
  onSort,
  sortBy,
  orderBy,
  onResend,
  onEdit,
  onAddCoApplicant,
  showActions = true,
}: InviteeTableProps) {
  const canShowActions = (invite: Invitee) => {
    return (
      invite.invite_accepted !== '1' &&
      invite.invite_accepted !== 1 &&
      invite.show_action_button === 1
    )
  }

  const canShowAddCoApplicant = (invite: Invitee) => {
    // Max 4 co-applicants allowed
    const coApplicantCount = invite.co_applicant?.length || 0;
    if (coApplicantCount >= 4) return false;
    // Show if can show actions OR if status allows progress
    return (
      canShowActions(invite) ||
      (invite.status && ALLOW_PROGRESS_STATUSES.includes(invite.status as any))
    )
  }

  const SortIcon = ({ column }: { column: string }) => {
    const isActive = orderBy === column
    return (
      <span
        aria-hidden="true"
        className={cn(styles.sortIcon, isActive && styles.sortIconActive)}
      >
        {isActive ? (sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  const SortHeader = ({ column, children }: { column: string; children: React.ReactNode }) => {
    return (
      <button
        type="button"
        className={styles.sortHeaderButton}
        onClick={(event) => {
          event.stopPropagation()
          onSort(column)
        }}
      >
        <span className={styles.sortHeaderLabel}>{children}</span>
        <SortIcon column={column} />
      </button>
    )
  }

  return (
    <div data-testid="invitee-list-table-card" className={cn(styles.tableCard, styles.inviteeListTableCard)}>
      <div
        data-testid="invitee-list-table-scroll"
        className={`${styles.tableResponsiveScroll} touch-scroll`}
      >
        <table
          data-testid="invitee-list-table"
          className={cn(
            styles.listingTable,
            styles.inviteeListingTable,
            /* Fit viewport: fixed layout + wrap; Tailwind reinforces padding/typography vs load order. */
            'table-fixed w-full min-w-0 max-w-full [&_thead_th]:!whitespace-normal [&_tbody_td:not([colspan])]:!whitespace-normal',
            /* Do not break/wrap the Action column header or controls — other columns may wrap */
            '[&_thead_th:not([data-col="actions"])]:break-words [&_tbody_td:not([colspan]):not([data-col="actions"])]:break-words',
            '[&_thead_th[data-col="actions"]]:!break-normal [&_thead_th[data-col="actions"]]:!whitespace-nowrap',
            '[&_tbody_td[data-col="actions"]]:!break-normal',
            '[&_tbody_td:not([colspan])]:!px-1.5 [&_tbody_td:not([colspan])]:!py-2 [&_tbody_td:not([colspan])]:!text-[12px] [&_tbody_td:not([colspan])]:!font-normal [&_tbody_td:not([colspan])]:!leading-snug',
          )}
        >
          <colgroup>
            {(showActions ? INVITEE_COL_WIDTHS_PCT_WITH_ACTION : INVITEE_COL_WIDTHS_PCT_NO_ACTION).map((pct, i) => (
              <col key={i} style={{ width: `${pct}%` }} />
            ))}
          </colgroup>
          <thead data-testid="invitee-list-table-head">
            <tr>
              <th data-testid="invitee-list-table-col-marker" />
              <th
                data-testid="invitee-list-table-sort-property-staff"
                className="cursor-pointer"
                onClick={() => onSort('agent_name')}
              >
                <SortHeader column="agent_name">Property Staff</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-applicant-name"
                className="cursor-pointer"
                onClick={() => onSort('applicant_name')}
              >
                <SortHeader column="applicant_name">Applicant Name</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-email"
                className="cursor-pointer"
                onClick={() => onSort('email')}
              >
                <SortHeader column="email">Email</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-mobile"
                className="cursor-pointer"
                onClick={() => onSort('mobile_no')}
              >
                <SortHeader column="mobile_no">Mobile Phone</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-property-name"
                className="cursor-pointer"
                onClick={() => onSort('property_name')}
              >
                <SortHeader column="property_name">Property Name</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-company-name"
                className="cursor-pointer"
                onClick={() => onSort('company_name')}
              >
                <SortHeader column="company_name">Company Name</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-sales-person"
                className="cursor-pointer"
                onClick={() => onSort('sales_manager')}
              >
                <SortHeader column="sales_manager">Sales Person</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-rent"
                className="cursor-pointer"
                onClick={() => onSort('rent_amount')}
              >
                <SortHeader column="rent_amount">Rent/Mo ($)</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-income"
                className="cursor-pointer"
                onClick={() => onSort('income')}
              >
                <SortHeader column="income">Income ($)</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-unit"
                className="cursor-pointer"
                onClick={() => onSort('unit_number')}
              >
                <SortHeader column="unit_number">Unit</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-status"
                className="cursor-pointer"
                onClick={() => onSort('status')}
              >
                <SortHeader column="status">Status</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-created"
                className="cursor-pointer"
                onClick={() => onSort('created_at')}
              >
                <SortHeader column="created_at">Created</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-status-updated"
                className="cursor-pointer"
                onClick={() => onSort('invite_status_date')}
              >
                <SortHeader column="invite_status_date">Status Updated</SortHeader>
              </th>
              <th
                data-testid="invitee-list-table-sort-invite-code"
                className="cursor-pointer"
                onClick={() => onSort('invite_code')}
              >
                <SortHeader column="invite_code">Invite Code</SortHeader>
              </th>
              {showActions && (
                <th
                  data-testid="invitee-list-table-col-actions"
                  data-col="actions"
                  className={styles.inviteeListActionCell}
                >
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody data-testid="invitee-list-table-body">
            {invitees.length === 0 ? (
              <TableSearchFilterEmptyTableRow
                colSpan={showActions ? 16 : 15}
                rowDataTestId="invitee-list-table-empty-row"
                data-testid="invitee-list-table-empty-message"
              />
            ) : (
              invitees.map((invite) => {
                const canAct = canShowActions(invite)
                const showAddCoApplicant = canShowAddCoApplicant(invite)

                const rowSuffix = String(invite.invite_code).replace(/[^a-zA-Z0-9_-]/g, '_')
                return (
                  <React.Fragment key={invite.invite_code}>
                    <tr data-testid={`invitee-list-table-row-primary-${rowSuffix}`}>
                      <td
                        data-testid={`invitee-list-table-row-primary-marker-${rowSuffix}`}
                        title="Primary Applicant"
                      >
                        <Minus className="h-4 w-4" />
                      </td>
                      <td>{(invite.agent_name ?? (invite as any).property_staff_name) || '-'}</td>
                      <td>{invite.applicant_name}</td>
                      <td>{invite.email}</td>
                      <td>{formatPhoneNumber(invite.mobile_no)}</td>
                      <td>{invite.property_name}</td>
                      <td>{invite.company_name || '-'}</td>
                      <td>{invite.sales_manager || '-'}</td>
                      <td>{formatCurrency(invite.rent_amount)}</td>
                      <td>{invite.income ? formatCurrency(invite.income) : '-'}</td>
                      <td>{invite.unit_number || '-'}</td>
                      <td>
                        <span>
                          {invite.status}
                        </span>
                      </td>
                      <td>{formatDateTimeForDisplay(invite.created_at)}</td>
                      <td>{invite.invite_status_date ? formatDateTimeForDisplay(invite.invite_status_date) : ''}</td>
                      <td className="font-mono">{invite.invite_code}</td>
                      {showActions && (
                        <td data-col="actions" className={styles.inviteeListActionCell}>
                          <div className={styles.inviteeListActionStack}>
                            <div className={styles.inviteeListActionStackRow}>
                              {canAct && onResend && (
                                <button
                                  type="button"
                                  data-testid={`invitee-list-table-resend-button-${rowSuffix}`}
                                  onClick={() => onResend(invite.invite_code)}
                                  className={styles.iconAction}
                                  title="Re-Send Invite"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              )}
                              {canAct && onEdit && (
                                <button
                                  type="button"
                                  data-testid={`invitee-list-table-edit-button-${rowSuffix}`}
                                  onClick={() => onEdit(invite.invite_code)}
                                  className={styles.textAction}
                                  title="Edit"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            {showAddCoApplicant && onAddCoApplicant && (
                              <button
                                type="button"
                                data-testid={`invitee-list-table-add-co-applicant-button-${rowSuffix}`}
                                onClick={() => onAddCoApplicant(invite)}
                                className={cn(styles.textAction, styles.inviteeListActionCoButton)}
                                title="Add Co-Applicant"
                              >
                                Add Co-Applicant
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>

                    {/* Co-applicant rows (Angular renders inline, not collapsible) */}
                    {invite.co_applicant?.map((coApp: any, index: number) => {
                      const canActCoApp = canShowActions(coApp)
                      const coCode =
                        coApp.invite_code != null && String(coApp.invite_code)
                          ? String(coApp.invite_code).replace(/[^a-zA-Z0-9_-]/g, '_')
                          : `${rowSuffix}-idx-${index}`
                      return (
                        <tr
                          key={`${invite.invite_code}-co-${index}`}
                          data-testid={`invitee-list-table-row-co-${coCode}`}
                        >
                          <td data-testid={`invitee-list-table-row-co-marker-${coCode}`} />
                          <td>{(invite.agent_name ?? (invite as any).property_staff_name) || '-'}</td>
                          <td>
                            {coApp.first_name} {coApp.last_name}
                          </td>
                          <td>{coApp.email}</td>
                          <td>{formatPhoneNumber(coApp.mobile_no)}</td>
                          <td>{invite.property_name}</td>
                          <td>{invite.company_name || '-'}</td>
                          <td>{invite.sales_manager || '-'}</td>
                          <td>{formatCurrency(invite.rent_amount)}</td>
                          <td>{coApp.income ? formatCurrency(coApp.income) : ''}</td>
                          <td>{invite.unit_number || '-'}</td>
                          <td>{coApp.status || invite.status || '-'}</td>
                          <td>
                            {coApp.created_at
                              ? formatDateTimeForDisplay(coApp.created_at)
                              : invite.created_at
                                ? formatDateTimeForDisplay(invite.created_at)
                                : ''}
                          </td>
                          <td>
                            {coApp.invite_status_date
                              ? formatDateTimeForDisplay(coApp.invite_status_date)
                              : invite.invite_status_date
                                ? formatDateTimeForDisplay(invite.invite_status_date)
                                : ''}
                          </td>
                          <td className="font-mono">{coApp.invite_code || '-'}</td>
                          {showActions && (
                            <td data-col="actions" className={styles.inviteeListActionCell}>
                              <div className={styles.inviteeListActionStack}>
                                <div className={styles.inviteeListActionStackRow}>
                                  {canActCoApp && onResend && coApp.invite_code && (
                                    <button
                                      type="button"
                                      data-testid={`invitee-list-table-co-resend-button-${coCode}`}
                                      className={styles.iconAction}
                                      onClick={() => onResend(coApp.invite_code)}
                                      title="Re-Send Invite"
                                    >
                                      <Send className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canActCoApp && onEdit && coApp.invite_code && (
                                    <button
                                      type="button"
                                      data-testid={`invitee-list-table-co-edit-button-${coCode}`}
                                      className={styles.textAction}
                                      onClick={() => onEdit(coApp.invite_code, invite.invite_code)}
                                      title="Edit"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
